import React, { useRef, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import SearchForm from './components/SearchForm';
import SearchListing from './components/SearchListing';
import ModeControls from './components/ModeControls';
import ViewControls from './components/ViewControls';

function App(props) {
  const { onReady } = props;

  const hasCalledReady = useRef(false);
  let coords = {};
  let houseTypes = [];
  let features = [];
  let buyingSchemes = [];

  if (props?.get?.lat && props?.get?.lng) {
    coords = {
      lat: parseFloat(props.get.lat),
      lng: parseFloat(props.get.lng),
    };
  }

  if (typeof props?.get?.houseTypes === 'string') {
    houseTypes = props.get.houseTypes.split(',').filter(Boolean);
  } else if (Array.isArray(props?.get?.houseTypes)) {
    houseTypes = props.get.houseTypes;
  }

  if (typeof props?.get?.features === 'string') {
    features = props.get.features.split(',').filter(Boolean);
  } else if (Array.isArray(props?.get?.features)) {
    features = props.get.features;
  }

  if (typeof props?.get?.buyingSchemes === 'string') {
    buyingSchemes = props.get.buyingSchemes.split(',').filter(Boolean);
  } else if (Array.isArray(props?.get?.buyingSchemes)) {
    buyingSchemes = props.get.buyingSchemes;
  }

  const [filters, setFilters] = useState({
    address: props?.get?.address || '',
    coordinates: coords,
    radius: props?.get?.r || '20',
    minPrice: props?.get?.minPrice || '',
    maxPrice: props?.get?.maxPrice || '',
    minBedrooms: props?.get?.minBedrooms || '',
    maxBedrooms: props?.get?.maxBedrooms || '',
    houseTypes: houseTypes,
    features: features,
    buyingSchemes: buyingSchemes,
    page: props?.get?.pg ? parseInt(props.get.pg, 10) : 1,
  });

  const [searchMode, setSearchMode] = useState(
    props.get?.mode || 'developments',
  );

  const [resultsMode, setResultsMode] = useState(
    props.get?.mode || 'developments',
  );

  const [viewMode, setViewMode] = useState(props.get?.view || 'grid');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Initial load on mount
  useEffect(() => {
    handleSearch(filters);
  }, []);

  // Call onReady when search results load
  useEffect(() => {
    if (!isSearching && searchResults.length > 0 && !hasCalledReady.current) {
      hasCalledReady.current = true;
      onReady?.();
    }
  }, [isSearching, searchResults, onReady]);

  useEffect(() => {
    if (!isSearching) {
      handleSearch(filters);
      buildQueryString(filters, resultsMode, viewMode);
    }
  }, [searchMode, resultsMode]);

  useEffect(() => {
    if (!isSearching) {
      buildQueryString(filters, searchMode, viewMode);
    }
  }, [viewMode]);

  function handleSearchModeChange(newMode) {
    setSearchMode(newMode);

    // Reset view mode to grid when switching away from developments
    if (newMode !== 'developments') {
      setViewMode('grid');
    }

    const resetFilters = {
      ...filters,
      page: 1,
      // When switching to developments, clear these filters as not applicable
      ...(newMode === 'developments'
        ? { houseTypes: [], features: [], buyingSchemes: [] }
        : {}),
    };
    setFilters(resetFilters);
    buildQueryString(resetFilters, newMode, viewMode);
    handleSearch(resetFilters, newMode);

    if (window.plausible) {
      window.plausible('Property Search - Search Mode Changed');
    }
  }

  function handleViewModeChange(newMode) {
    setViewMode(newMode);
    buildQueryString(filters, searchMode, newMode);
  }

  const handleSearch = async (searchFilters, mode = searchMode) => {
    setIsSearching(true);
    try {
      const response = await fetch(`/wp-json/property-search/${mode}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(searchFilters),
      });
      const data = await response.json();
      setSearchResults(data);
      setResultsMode(mode);
      if (window.plausible) {
        window.plausible('Property Search - Search Performed');
      }
      return data;
    } catch (error) {
      console.error('Search failed:', error);
      return null;
    } finally {
      setIsSearching(false);
    }
  };

  const clearFilters = async () => {
    const defaultFilters = {
      address: '',
      coordinates: null,
      radius: '20',
      minPrice: '',
      maxPrice: '',
      minBedrooms: '',
      maxBedrooms: '',
      houseTypes: [],
      features: [],
      buyingSchemes: [],
    };

    const hasNoResults =
      !searchResults?.posts || searchResults.posts.length === 0;

    if (hasNoResults && searchMode !== 'developments') {
      setSearchMode('developments');
      setFilters(defaultFilters);
      await handleSearch(defaultFilters, 'developments');
      return;
    }

    setFilters(defaultFilters);
    handleSearch(defaultFilters);
  };

  const buildQueryString = (filters) => {
    const params = new URLSearchParams();
    if (filters.address) params.set('address', filters.address);
    if (filters.coordinates) {
      if (filters.coordinates.lat && filters.coordinates.lng) {
        params.set('lat', filters.coordinates.lat.toString());
        params.set('lng', filters.coordinates.lng.toString());
        if (window.plausible) {
          window.plausible('Property Search - Address Changed');
        }
      }
    }
    if (filters.radius !== '20') {
      params.set('r', filters.radius);
      if (window.plausible) {
        window.plausible('Property Search - Radius Filter Changed');
      }
    }
    if (filters.minPrice){ params.set('minPrice', filters.minPrice); if (window.plausible) { window.plausible('Property Search - Min Price Filter Changed'); } }
    if (filters.maxPrice){ params.set('maxPrice', filters.maxPrice); if (window.plausible) { window.plausible('Property Search - Max Price Filter Changed'); } }
    if (filters.minBedrooms) { params.set('minBedrooms', filters.minBedrooms); if (window.plausible) { window.plausible('Property Search - Min Bedrooms Filter Changed'); } }
    if (filters.maxBedrooms) { params.set('maxBedrooms', filters.maxBedrooms); if (window.plausible) { window.plausible('Property Search - Max Bedrooms Filter Changed'); } }
    if (filters.houseTypes.length) {
      params.set('houseTypes', filters.houseTypes.join(','));
      if (window.plausible) {
        window.plausible('Property Search - House Type Filter Changed');
      }
    }
    if (filters.features.length) {
      params.set('features', filters.features.join(','));
      if (window.plausible) {
        window.plausible('Property Search - Feature Filter Changed');
      }
    }
    if (filters.buyingSchemes.length) {
      params.set('buyingSchemes', filters.buyingSchemes.join(','));
      if (window.plausible) {
        window.plausible('Property Search - Buying Scheme Filter Changed');
      }
    }
    if (filters.page && filters.page > 1) {
      params.set('pg', filters.page.toString());
      if (window.plausible) {
        window.plausible('Property Search - Page Changed');
      }
    }
    if (searchMode) {
      params.set('mode', searchMode);
      if (window.plausible) {
        window.plausible('Property Search - Mode Changed');
      }
    }
    if (viewMode) {
      params.set('view', viewMode);
      if (window.plausible) {
        window.plausible('Property Search - View Mode Changed');
      }
    }

    setSearchParams(params, { replace: true });

    return params.toString();
  };

  const handlePageChange = (page) => {
    const newFilters = { ...filters, page };
    setFilters(newFilters);
    buildQueryString(newFilters, searchMode, viewMode);
    handleSearch(newFilters);

    if (window.plausible) {
      window.plausible('Property Search - Page Changed');
    }
  };

  return (
    <>
      <section>
        <div className="container search-form-container">
          <SearchForm
            {...props}
            searchMode={searchMode}
            filters={filters}
            setFilters={setFilters}
            onSearch={handleSearch}
            isSearching={isSearching}
            buildQueryString={buildQueryString}
            viewMode={viewMode}
          />
        </div>
      </section>

      <section>
        <div className="container 2xl:max-w-[1228px]">
          <div className="flex justify-between mt-8 md:mt-0">
            <ModeControls
              searchMode={searchMode}
              searchModes={props.searchModes}
              onSearchModeChange={handleSearchModeChange}
            />
          </div>
        </div>
      </section>

      <section className="bg-grey-100 py-8 pb-12 md:pb-31 min-h-screen">
        <div className="container 2xl:max-w-[1228px] 2xl:px-0">
          <SearchListing
            resultsMode={resultsMode}
            searchMode={searchMode}
            viewMode={viewMode}
            viewModes={props.viewModes}
            onViewModeChange={handleViewModeChange}
            filters={filters}
            results={searchResults}
            clearFilters={clearFilters}
            onPageChange={handlePageChange}
            isSearching={isSearching}
          />
        </div>
      </section>
    </>
  );
}

export default App;
